import { useThemeMode } from '@/theme/theme-provider';

/*
 * The artwork lives in `public/` and is referenced by URL rather than imported,
 * so the same files serve the favicon in `index.html` and these components. One
 * copy of the mark, one place to replace it.
 */
export const LOGOMARK = '/logomark.png';
export const LOGO = '/logo.png';
/** The same lockup with a white wordmark, for dark surfaces. */
export const LOGO_DARK = '/logo_dark.png';

/**
 * The cross on its own, at whatever size the caller needs.
 *
 * Rendered on a plain surface with no tinted plate behind it: the mark already
 * carries two greens and a pale leaf, and setting it on a filled brand square
 * — which is what the placeholder icon needed — muddies the light arm into the
 * background it is supposed to read against.
 */
export function Logomark({ size = 30 }: { size?: number }) {
  return (
    <img
      src={LOGOMARK}
      alt=""
      width={size}
      height={size}
      className="shrink-0 select-none"
      style={{ width: size, height: size, objectFit: 'contain' }}
      // Chrome for the app shell; it is decorative beside the wordmark below.
      aria-hidden
      draggable={false}
    />
  );
}

/**
 * The full horizontal lockup.
 *
 * Two files, because the wordmark cannot be recoloured by CSS without touching
 * the cross beside it: `logo.png` sets it in the brand ink for light surfaces,
 * `logo_dark.png` in white for dark ones. The cross is identical in both, so
 * only the word changes as the theme does.
 *
 * Constrain by `height` inside a fixed-height row — the app shell's brand row
 * is 52px and the artwork is 3.2:1, so a width that looks right on the sign-in
 * card overflows the sider. `maxWidth` is for the sign-in screen, where the
 * card's width is what bounds it.
 */
export function BrandLockup({ maxWidth, height }: { maxWidth?: number; height?: number }) {
  const { mode } = useThemeMode();

  return (
    <img
      src={mode === 'dark' ? LOGO_DARK : LOGO}
      alt="The Local Pharmacy"
      className="select-none"
      style={
        height
          ? { height, width: 'auto' }
          : { width: '100%', height: 'auto', maxWidth: maxWidth ?? 240 }
      }
      draggable={false}
    />
  );
}
